import React, {useContext, useMemo} from 'react';
import {useSelector} from 'react-redux';

import clsx from 'clsx';
import {CopyButton} from 'src/components/copy-button';
import {AccountLink} from 'src/components/links';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {useCountries, useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {selectUserDetails} from 'src/modules/profile/user/services/user-page-slice';
import {selectUserInformation} from 'src/modules/profile/user/services/user-profile-slice';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormSection} from '@pb/kit-component-form-section';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons, Sizes} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';
import {LocaleContext} from '@pb/kit-context-locale';

import {ChangeAccount} from '../change-account';


import {UserInformationSkeleton} from './user-information-skeleton';

import {
    userInformationSectionHeader,
    userInformationSectionHeaderIcon,
    userInformationSectionUserState,
    userInformationSectionUserStateActive,
} from './user-information-section.module.styl';

type Props = {
    userId: number,
};

export const UserInformationSection: React.FC<Props> = ({userId}) => {
    const {locale} = useContext(LocaleContext);

    const {userStatuses} = useUserStatuses();
    const {userTypes} = useUserTypes();
    const {countries} = useCountries();

    const information = useSelector(selectUserInformation);
    const {authInfo} = useSelector(selectUserDetails);


    const userStatusClasses = clsx(userInformationSectionUserState, [
        information?.statusCode === 'ENABLED' && userInformationSectionUserStateActive,
    ]);

    const status = useMemo(
        () => userStatuses.find(({code}) => code === information?.statusCode)?.description || '--',
        [userStatuses, information],
    );

    const country = useMemo(
        () => countries.find(({code}) => code === information?.countryCode)?.description || '--',
        [countries, information],
    );

    const type = useMemo(
        () => userTypes.find(({code}) => code === information?.typeCode)?.description || '--',
        [userTypes, information],
    );

    return (
        <Skeleton view={<UserInformationSkeleton/>} shown={!information}>
            <FormSection
                headline="User Information"
                info={headline => (
                    <Item grow={1}>
                        <span className={userInformationSectionHeader}>
                            {headline}
                            <strong className={userStatusClasses}>
                                {status}
                            </strong>
                            <span className={userInformationSectionHeaderIcon}>
                                <Icon type={Icons.ADDRESS} size={Sizes.Size_16}/>
                            </span>
                        </span>
                    </Item>
                )}>
                <Group justify="space-between" alignItems="start">
                    <Group justify="start" direction="v" gap="s">
                        <Group>
                            <WidthItem width={70}><strong>User ID</strong></WidthItem>
                            <WidthItem width={120}>
                                {information?.id ? (
                                    <Group>
                                        {information.id}
                                        <CopyButton copiedText={information.id.toString()} entityName="User ID"/>
                                    </Group>
                                ) : '--'}
                            </WidthItem>
                        </Group>
                        <Group>
                            <WidthItem width={70}><strong>Email</strong></WidthItem>
                            <WidthItem width={120}>
                                {information?.email ? (
                                    <Group gap="xs">
                                        <EllipsisTextHint title={information.email}>
                                            {information.email}
                                        </EllipsisTextHint>
                                        <CopyButton copiedText={information.email} entityName="Email"/>
                                    </Group>
                                ) : '--'}
                            </WidthItem>
                        </Group>
                        <Group>
                            <WidthItem width={70}><strong>Job title</strong></WidthItem>
                            <WidthItem width={140}>
                                <EllipsisTextHint title={information?.jobTitle || '--'}>
                                    {information?.jobTitle || '--'}
                                </EllipsisTextHint>
                            </WidthItem>
                        </Group>
                        <Group>
                            <WidthItem width={70}><strong>Country</strong></WidthItem>
                            <WidthItem width={140}>
                                <EllipsisTextHint title={country}>{country}</EllipsisTextHint>
                            </WidthItem>
                        </Group>
                        <Group>
                            <WidthItem width={70}>
                                <Group gap="xs" alignItems="center">
                                    <strong>M* GUID</strong>
                                    <Tooltip
                                        color="light"
                                        placement="right"
                                        anchor={({getAnchorProps, anchorRef}) => (
                                            <Icon ref={anchorRef} type={Icons.INFO} {...getAnchorProps()}/>
                                        )}>
                                        <div style={{width: 250}}>
                                            Morningstar GUID (Globally Unique Identifier) is a unique
                                            32-character identifier.
                                            You can assign a GUID in the <b>Login & Password</b> section.
                                        </div>
                                    </Tooltip>
                                </Group>
                            </WidthItem>
                            <WidthItem width={120}>
                                {authInfo?.data ? (
                                    <div>
                                        {authInfo.data.guid ? (
                                            <Group>
                                                <EllipsisTextHint title={authInfo.data.guid || '--'}>
                                                    {authInfo.data.guid}
                                                </EllipsisTextHint>
                                                <CopyButton copiedText={authInfo.data.guid} entityName="User GUID"/>
                                            </Group>
                                        ) : <span className="negative">Missing</span>}
                                    </div>
                                ) : null}
                                {authInfo?.error ? (
                                    <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                                        <b ref={anchorRef} {...getAnchorProps()}>
                                            <Icon type={Icons.ERROR}/> Can't load M* GUID
                                        </b>
                                    )}>
                                        {authInfo.error.message}
                                    </Tooltip>
                                ) : null}
                            </WidthItem>
                        </Group>
                    </Group>
                    <Group justify="start" direction="v" gap="s">
                        <Group>
                            <WidthItem width={90}><strong>Account</strong></WidthItem>
                            {information?.account ? (
                                <Group gap="xs">
                                    <WidthItem maxWidth={120}>
                                        <AccountLink
                                            place="User Information"
                                            accountId={information.account.accountId}
                                            accountName={information.account.accountName}
                                        />
                                    </WidthItem>
                                    <ChangeAccount userId={userId} accountId={information.account.accountId}/>
                                </Group>
                            ) : '--'}
                        </Group>
                        <Group>
                            <WidthItem width={90}><strong>Type</strong></WidthItem>
                            <WidthItem width={120}>{type}</WidthItem>
                        </Group>
                        <Group>
                            <WidthItem width={90}><strong>Status</strong></WidthItem>
                            <WidthItem width={120}>{status}</WidthItem>
                        </Group>
                        <Group>
                            <WidthItem width={90}><strong>Group</strong></WidthItem>
                            <WidthItem width={120}>
                                <EllipsisTextHint title={information?.groupName || '--'}>
                                    {information?.groupName || '--'}
                                </EllipsisTextHint>
                            </WidthItem>
                        </Group>
                        <Group>
                            <WidthItem width={90}><strong>First licensed</strong></WidthItem>
                            <WidthItem width={120}>
                                {information?.firstLicense ? FormattedDate.format({
                                    value: information.firstLicense,
                                    format: mapDateFormat(locale.dateFormat, 'WORD'),
                                }) : '--'}
                            </WidthItem>
                        </Group>
                    </Group>
                </Group>
            </FormSection>
        </Skeleton>
    );
};
